/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState } from 'react';
import { get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import Slider from '@mui/material/Slider';
import Input from '@mui/material/Input';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { FaMinusCircle } from 'react-icons/fa';
import { useDebouncedCallback } from 'use-debounce';
import Autocomplete from '@mui/material/Autocomplete';
import format from 'date-fns/format';

import {
  CONTRAST, QUARTER_CONTRAST, INFO, ERROR, SUCCESS,
} from '../../theme';

import { Picker } from '../../ui';

const SliderWrapper = styled.div`
  margin-left: 8px;
  margin-right: 16px;
  width: calc(100% - 29px);
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 8px;
  margin-right: 16px;
`;

const inputStyle = {
  border: '1px solid black',
  borderRadius: '3px',
  paddingLeft: '6px',
  minWidth: '62px',
};

const FilterHeader = styled.div`
  color: ${CONTRAST};
  font-size: 18px;
  font-weight: 600;
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FilterLabel = styled.div`
  color: ${CONTRAST};
  padding-bottom: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Pill = styled.div`
  cursor: pointer;
  color: ${CONTRAST};
  padding: 8px;
  margin: 0 4px 4px 0;
  border: 1px solid ${QUARTER_CONTRAST};
  border-radius: 3px;
  font-size: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const AreaContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0 10px 0 0;
`;

const AreaToggle = styled.div`
  cursor: pointer;
  padding: 8px;
  margin: 0 6px 4px 0;
  border: 1px solid
    ${({ active, color = INFO }) => (active ? color : QUARTER_CONTRAST)};
  border-radius: 3px;
  font-size: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  opacity: ${({ active }) => (active ? 1 : 0.5)};
  font-weight: bold;
  pointer-events: ${({ active }) => (active ? 'auto' : 'none')};
  color: ${({ active }) => (active ? 'white' : CONTRAST)};
  background-color: ${({ active, color = INFO }) => (active ? color : 'transparent')};
`;
const PillList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 8px 0 0;
`;
const MinusCircle = styled(FaMinusCircle)`
  padding-left: 8px;
`;

const FilterContainer = styled.div`
  color: ${CONTRAST};
  padding-bottom: 16px;
  width: 100%;
`;

const getStep = ({ min, max }) => {
  const diff = max - min;
  if (diff < 0.1) {
    return 0.001;
  }
  if (diff < 1) {
    return 0.01;
  }
  if (diff < 5) {
    return 0.1;
  }
  if (diff < 1000) {
    return 1;
  }
  if (diff < 10000) {
    return 10;
  }
  if (diff < 100000) {
    return 100;
  }
  return 100;
};

const mapState = ({ drawArea, drawEnabled, dynamicFilters }) => ({
  drawArea, drawEnabled, dynamicFilters,
});
const toLabel = (rawNum) => {
  const num = Math.round(rawNum * 1000) / 1000;

  return Math.abs(num) > 999
    ? `${Math.sign(num) * Math.round(Math.abs(num) / 1000).toFixed(1)}k`
    : Math.sign(num) * Math.abs(num);
};

const TRANSFORMS_TO_DISPLAY = {
  PERCENT: (num) => Math.round(parseFloat(num) * 1000) / 10,
  INTEGER: (num) => Math.round(parseInt(num, 10)),
};

const TRANSFORMS_TO_SAVE = {
  PERCENT: (num) => parseFloat(num) / 100,
  INTEGER: (num) => Math.round(parseInt(num, 10)),
};

const UNIT_PREPEND = {
  DOLLARS: '$',
};

const UNIT_POSTPEND = {
  PERCENT: '%',
};

export const Filter = ({
  filterKey,
  label,
  options,
  type,
  onChange,
  filtersSelected,
  min,
  max,
  unit,
  operator,
}) => {
  const dispatch = useDispatch();
  const { drawArea, drawEnabled, dynamicFilters } = useSelector(mapState);
  if (type === 'TITLE') {
    return (
      <FilterHeader>{label}</FilterHeader>
    );
  }
  if (type === 'AREA') {
    return (
      <FilterContainer>
        <FilterLabel>{label}</FilterLabel>
        <AreaContainer>
          <AreaToggle
            active
            color={drawEnabled ? ERROR : INFO}
            onClick={() => {
              if (drawEnabled) {
                dispatch({ type: 'DRAW_DISABLED' });
                dispatch({ type: 'CLEAR_DRAW_AREA' });
                onChange('EMPTY', filterKey, 'inside');
              } else {
                dispatch({ type: 'DRAW_ENABLED' });
              }
            }}
          >
            {drawEnabled ? 'Clear Drawing' : 'Start Drawing'}
          </AreaToggle>
          <AreaToggle
            color={SUCCESS}
            active={drawArea}
            onClick={() => {
              onChange(drawArea, filterKey, 'inside');
            }}
          >
            Filter Layer by Drawing
          </AreaToggle>
        </AreaContainer>
      </FilterContainer>
    );
  }
  if (type === 'SELECT') {
    if (!options || !options.length || options.length === 1) return null;
    return (
      <FilterContainer>
        <FilterLabel>{label}</FilterLabel>
        <Picker
          options={options}
          onChange={(e) => onChange(e.target.value, filterKey, operator)}
          value={get(filtersSelected, `${filterKey}.${operator || 'eq'}`) || 'EMPTY'}
        />
      </FilterContainer>
    );
  }
  if (type === 'TEXT') {
    const selectedFilters = get(filtersSelected, `${filterKey}.${operator || 'eq'}`, []);
    const [text, setText] = useState('');

    return (
      <FilterContainer>
        <FilterLabel>{label}</FilterLabel>
        <div style={{ flexDirection: 'row', display: 'flex' }}>
          <TextField
            variant="outlined"
            placeholder={label}
            onChange={(e) => setText(e.target.value || '')}
            value={text}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange([...selectedFilters, text], filterKey, operator);
                setText('');
              }
            }}
          />
          <Button
            onClick={() => {
              onChange([...selectedFilters, text], filterKey, operator);
              setText('');
            }}
          >
            Add
          </Button>
        </div>

        <PillList>
          {selectedFilters.map((selectedFilterValue) => (
            <Pill
              key={selectedFilterValue}
              onClick={() => {
                const values = selectedFilters.filter(
                  (val) => selectedFilterValue != val,
                );
                onChange(values.length ? values : 'EMPTY', filterKey, operator);
              }}
            >
              {selectedFilterValue}
              <MinusCircle />
            </Pill>
          ))}
        </PillList>
      </FilterContainer>
    );
  }
  if (type === 'MULTISELECT' || type === 'DATA_STREAM_MULTISELECT') {
    if (!options || !options.length || options.length === 1) return null;
    const selectedFilters = get(filtersSelected, `${filterKey}.${operator || 'eq'}`, []);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');

    return (
      <FilterContainer>
        <FilterLabel>{label}</FilterLabel>
        <Autocomplete
          {
            ...{ 'data-test': label }
          }
          options={options
            .filter(
              (opt) => selectedFilters.indexOf(opt.value) === -1 && opt.value != null,
            )
            .map((opt) => ({
              label: `${opt.label}`,
              value: `${opt.value}`,
            }))}
          getOptionLabel={(option) => `${option.label}` || ''}
          style={{
            paddingRight: 16,
          }}
          value={value}
          inputValue={inputValue}
          onChange={(e, selectedValue) => {
            setInputValue('');
            setValue(null);
            onChange([...selectedFilters, selectedValue.value], filterKey, operator);
          }}
          onInputChange={(event, newValue) => {
            setInputValue(newValue);
          }}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              placeholder={`Search ${label}`}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
                style: {
                  padding: 3,
                  margin: 0,
                },
              }}
            />
          )}
        />
        <PillList>
          {selectedFilters.map((selectedFilterValue) => (
            <Pill
              key={selectedFilterValue}
              onClick={() => {
                const values = selectedFilters.filter(
                  (val) => selectedFilterValue != val,
                );
                onChange(values.length ? values : 'EMPTY', filterKey);
              }}
            >
              {
                (options
                  .find((option) => selectedFilterValue == option.value) || {})
                  .label
              }
              <MinusCircle />
            </Pill>
          ))}
        </PillList>
      </FilterContainer>
    );
  }
  if (type === 'DATE_RANGE' || type === 'DATA_STREAM_DATE_RANGE') {
    if (!min || !max) return null;
    const hasValue = Boolean(
      get(
        filtersSelected,
        `${filterKey}.gte`,
        get(filtersSelected, `${filterKey}.lte`),
      ),
    );

    const gte = format(new Date(get(filtersSelected, `${filterKey}.gte`, min)), 'y-MM-dd');
    const lte = format(new Date(get(filtersSelected, `${filterKey}.lte`, max)), 'y-MM-dd');

    const [handleMinInputChange] = useDebouncedCallback((event) => {
      onChange(event.target.value, filterKey, 'gte');
    }, 300);

    const [handleMaxInputChange] = useDebouncedCallback((event) => {
      onChange(event.target.value, filterKey, 'lte');
    }, 300);

    const clear = () => {
      onChange('EMPTY', filterKey, 'gte');
      onChange('EMPTY', filterKey, 'lte');
    };

    return (
      <FilterContainer>
        <FilterLabel>
          {label}
          {hasValue ? <MinusCircle onClick={clear} /> : null}
        </FilterLabel>
        <InputWrapper>
          <TextField
            variant="outlined"
            id="min-date"
            label="From"
            type="date"
            defaultValue={gte}
            inputProps={{
              style: { padding: 8 },
            }}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleMinInputChange}
          />
        </InputWrapper>
        <InputWrapper>
          <TextField
            variant="outlined"
            id="max-date"
            label="To"
            type="date"
            defaultValue={lte}
            inputProps={{
              style: { padding: 8 },
            }}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleMaxInputChange}
          />
        </InputWrapper>
      </FilterContainer>
    );
  }
  if (type === 'MIN_MAX' || type === 'MIN_MAX_SLIDER' || type === 'DATA_STREAM_MIN_MAX') {
    if (min == null || max == null) return null;
    const toDisplay = TRANSFORMS_TO_DISPLAY[unit] || (val => val);
    const toSave = TRANSFORMS_TO_SAVE[unit] || (val => val);
    const prepend = UNIT_PREPEND[unit] || '';
    const postpend = UNIT_POSTPEND[unit] || '';

    const round = (num) => (Math.floor(num * 100) / 100);
    const hasValue = Boolean(
      get(
        filtersSelected,
        `${filterKey}.gte`,
        get(filtersSelected, `${filterKey}.lte`),
      ),
    );
    const gte = toDisplay(parseFloat(get(filtersSelected, `${filterKey}.gte`, min)));
    const lte = toDisplay(parseFloat(get(filtersSelected, `${filterKey}.lte`, max)));

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [localMax, setLocalMax] = useState(lte);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [localMin, setLocalMin] = useState(gte);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [handleChange] = useDebouncedCallback((g, l) => {
      onChange(toSave(g), filterKey, 'gte');
      onChange(toSave(l), filterKey, 'lte');
    }, 300);

    const [handleMinInputChange] = useDebouncedCallback((event) => {
      onChange(toSave(event.target.value), filterKey, 'gte');
    }, 300);

    const [handleMaxInputChange] = useDebouncedCallback((event) => {
      onChange(toSave(event.target.value), filterKey, 'lte');
    }, 300);

    const clear = () => {
      onChange('EMPTY', filterKey, 'gte');
      onChange('EMPTY', filterKey, 'lte');
      setLocalMin(toDisplay(min));
      setLocalMax(toDisplay(max));
    };

    return (
      <FilterContainer>
        <FilterLabel>
          {label}
          {hasValue ? <MinusCircle onClick={clear} /> : null}
        </FilterLabel>
        <SliderWrapper>
          <Slider
            min={toDisplay(parseFloat(min))}
            max={toDisplay(parseFloat(max))}
            value={[localMin, localMax]}
            onChange={(_, [g, l]) => {
              setLocalMax(l);
              setLocalMin(g);
              handleChange(g, l);
            }}
            step={getStep({ min, max })}
            valueLabelDisplay="auto"
            aria-labelledby="range-slider"
            valueLabelFormat={(newValue) => toLabel(newValue)}
          />
        </SliderWrapper>
        <InputWrapper>
          <Input
            style={inputStyle}
            value={round(localMin)}
            onChange={(event) => {
              setLocalMin(event.target.value);
              handleMinInputChange(event);
            }}
            inputProps={{
              step: 1,
              min: toDisplay(min),
              max: toDisplay(max),
              type: 'number',
              'aria-labelledby': 'input-slider',
            }}
          />
          <Input
            style={inputStyle}
            value={round(localMax)}
            onChange={(event) => {
              setLocalMax(event.target.value);
              handleMaxInputChange(event);
            }}
            inputProps={{
              step: 1,
              min: toDisplay(min),
              max: toDisplay(max),
              type: 'number',
              'aria-labelledby': 'input-slider',
            }}
          />
        </InputWrapper>
        <FilterLabel>
          {localMin && localMax
            ? `Between ${prepend}${round(localMin).toLocaleString('en-US')}${postpend} and ${prepend}${round(localMax).toLocaleString('en-US')}${postpend}`
            : `${localMin ? `Over ${prepend}${round(localMin).toLocaleString('en-US')}${postpend}.` : ''}${localMax ? `Under ${prepend}${round(localMax).toLocaleString('en-US')}.${postpend}` : ''
            }`}
        </FilterLabel>
      </FilterContainer>
    );
  }

  if (type === 'AUTOCOMPLETE') {
    const selectedFilters = get(filtersSelected, `${filterKey}.eq`, []);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');

    const [handleInputChange] = useDebouncedCallback((newValue) => {
      dispatch({
        type: 'FETCH_AUTOCOMPLETE_OPTIONS',
        payload: {
          searchTerm: newValue,
          filterKey,
        },
      });
    }, 500);

    return (
      <FilterContainer>
        <FilterLabel>{label}</FilterLabel>
        <Autocomplete
          options={dynamicFilters[filterKey] || []}
          style={{ paddingRight: 16 }}
          value={value}
          inputValue={inputValue}
          clearOnBlur={false}
          onChange={(e, selectedValue) => {
            setInputValue('');
            setValue(null);
            onChange([...selectedFilters, selectedValue], filterKey);
          }}
          onInputChange={(event, newValue) => {
            if (newValue !== null || newValue !== '') {
              setInputValue(newValue);
              handleInputChange(newValue);
            }
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          renderOption={(props, option) => <div {...props}>{option}</div>}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              placeholder={`Search ${label}`}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
          )}
        />
        <PillList>
          {selectedFilters.map((selectedFilterValue) => (
            <Pill
              key={selectedFilterValue}
              onClick={() => {
                const values = selectedFilters.filter(
                  (val) => selectedFilterValue != val,
                );
                onChange(values.length ? values : 'EMPTY', filterKey);
              }}
            >
              {selectedFilterValue}
              <MinusCircle />
            </Pill>
          ))}
        </PillList>
      </FilterContainer>
    );
  }

  return null;
};
